import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link as LinkIcon, Pause, Play, ChevronUp, ChevronDown, Gavel, User as UserIcon, MapPin, PawPrint, Package } from 'lucide-react';

import { useHotkeys } from '../../hooks/useHotkeys';
import { useKeyboardSurface, useSurface } from '../../hooks/useSurface';
import { soundService } from '../../services/soundService';
import { Avatar } from './Avatar';
import { Kbd } from './Kbd';
import { StageLayout } from './StageLayout';
import { TALLY, STAGE, EASE, DUR, SPRING_SORT, SPRING_CHIP, prefersReducedMotion, rowCenterY } from './motion';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface TallyCell {
  answer: string;
  /**
   * 0 / 5 / 10 normally. Can be NEGATIVE (-5) when the `strictScoring` house
   * rule is on, and can be any of those values after a host override — which
   * is why this is a plain number and not a union.
   */
  points: number;
  /**
   * `override` — the host settled it by hand. It is emphatically NOT "invalid"
   * any more, so it must stop rendering struck-through and dimmed while showing
   * a green 10.
   */
  /**
   * `banned` — a real word, off the board this round (the Ladder's ban list).
   *
   * It is NOT `invalid`, and the difference is the whole point of the mechanic:
   * "Cat isn't an animal" would be a lie, and telling a nine-year-old their word
   * was wrong when it was merely *illegal tonight* is exactly the kind of thing
   * this codebase has spent a sprint stamping out. It scores 0, it is dimmed like
   * a word that scored nothing — and it carries its own reason, which the tally
   * shows verbatim: "Banned this round."
   */
  status: 'blank' | 'invalid' | 'duplicate' | 'unique' | 'override' | 'banned';
  duplicateOf: string[];
  /** Optional server-supplied reason, e.g. "doesn't start with S". */
  reason?: string;
  /** The host settled this cell by hand. */
  overridden?: boolean;
}

export interface TallyPlayer {
  id: string;
  name: string;
  avatar: string;
  isBot?: boolean;
}

export interface TallyRoundData {
  roundNumber: number;
  letter: string;
  scores: Record<string, number>;
  playerTotalScores: Record<string, number>;
  answerDetails: Record<string, Record<string, TallyCell>>;
}

export interface TallyCascadeProps {
  letter: string;
  categories: string[];
  roundData: TallyRoundData;
  /** In seat order — the SAME order every category. Predictability is what
   *  builds the "…and now MY answer" anticipation. */
  players: TallyPlayer[];
  isHost: boolean;
  /** Fired once the bridge has run out. */
  onComplete: () => void;
  /** True on the final round — the bridge says "See the winner" instead. */
  isLastRound?: boolean;
  /**
   * THE HOST OVERRIDE. The validator will always be wrong about *something*
   * ("orangatang", a cousin's name, a regional word). Rather than chase a
   * perfect wordlist, we let the host settle it at the table — which is how
   * the paper game has always worked.
   *
   * This must actually reach the server. A previous version of this control
   * changed the number on the host's own phone and nowhere else, which is
   * strictly worse than having no control at all: it *looks* like the argument
   * is settled while every other phone still disagrees.
   */
  onOverride?: (playerId: string, category: string, points: number) => void;
  /**
   * How fast the timeline advances. 1 = the phone's beat sheet, untouched.
   *
   * The TV runs at `TV_PACE` (0.75), which stretches the 900ms row cadence to
   * a 1.2s one. It is NOT a per-surface retune of the comedy — it is the same
   * beat sheet played slower, because a screen across the room is read slower
   * than one in your hand.
   *
   * CRITICAL: when a TV is connected, the PHONES run at this same rate too.
   * Every client's cascade is a deterministic function of the identical
   * `roundEnded` payload, so identical pace ⇒ identical beat ⇒ the host's
   * "next round" lands exactly when the television finishes laughing. Slow
   * only the TV and the phones would race ahead and cut the show off.
   */
  paceScale?: number;
  /**
   * THIS IS A TELEVISION.
   *
   * Nobody presses Space on a wall, nobody clicks it, and nobody wants to read
   * "Click to skip ahead" across a living room. In broadcast mode the cascade
   * loses every interactive affordance and becomes what it is on a TV: a show.
   *
   * The controls do not disappear from the game — they move to the host's
   * phone, which is exactly where the design says the remote belongs.
   */
  broadcast?: boolean;
}

/** The television's beat. 900ms × (1 / 0.75) = a 1.2s row cadence. */
export const TV_PACE = 0.75;

/** The three values a host can award. Negative scores are the validator's job, not the host's. */
const OVERRIDE_CHOICES = [0, 5, 10];

/** Chips can go negative under `strictScoring`; pick a legible colour for each band. */
type ChipKind = 'green' | 'amber' | 'slate' | 'danger';
function chipKindFor(points: number): ChipKind {
  if (points < 0) return 'danger';
  if (points >= 10) return 'green';
  if (points === 5) return 'amber';
  return 'slate';
}
/** `.score.danger` doesn't exist in the shared stylesheet (another agent owns it), so inline it. */
const DANGER_CHIP_STYLE: React.CSSProperties = {
  background: 'var(--coral-soft, rgba(185,74,59,0.14))',
  color: 'var(--coral, #b94a3b)',
};

const CATEGORY_ICON: Record<string, React.FC<{ size?: number; color?: string }>> = {
  Name: ({ size = 16, color }) => <UserIcon size={size} color={color} />,
  Place: ({ size = 16, color }) => <MapPin size={size} color={color} />,
  Animal: ({ size = 16, color }) => <PawPrint size={size} color={color} />,
  Thing: ({ size = 16, color }) => <Package size={size} color={color} />,
};

const BLANK_CELL: TallyCell = { answer: '', points: 0, status: 'blank', duplicateOf: [] };

/* ------------------------------------------------------------------ */
/* Clock — one pausable, skippable timeline                            */
/* ------------------------------------------------------------------ */

/**
 * A monotonic ms clock that can be paused and scrubbed. Everything the
 * cascade renders is a pure function of this number, which is what makes
 * the reveal *deterministic*: every client runs the identical timeline off
 * the identical `roundEnded` payload, so the whole table sees the same
 * beat. No extra server events required.
 */
function useClock(running: boolean, rate = 1): [number, (ms: number | ((m: number) => number)) => void] {
  const [ms, setMs] = useState(0);
  const raf = useRef<number | null>(null);
  const last = useRef(0);
  // Read the rate off a ref so changing it never restarts the clock.
  const rateRef = useRef(rate);
  rateRef.current = rate;

  useEffect(() => {
    if (!running) return;
    last.current = performance.now();
    const tick = (now: number) => {
      const dt = now - last.current;
      last.current = now;
      // THE ONE KNOB. Every beat in the cascade is a pure function of this
      // number, so scaling how fast it advances scales the ENTIRE timeline —
      // rows, chips, collisions, dwells, the bridge — with one multiplication
      // and no retuning. A television at the end of a room is further from
      // your eye than a phone in your hand, and the laugh needs longer to
      // travel; `rate < 1` is what buys it that time.
      setMs(m => m + dt * rateRef.current);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current !== null) cancelAnimationFrame(raf.current);
      raf.current = null;
    };
  }, [running]);

  return [ms, setMs];
}

/* ------------------------------------------------------------------ */
/* Schedule                                                            */
/* ------------------------------------------------------------------ */

export interface Row {
  player: TallyPlayer;
  cell: TallyCell;
  seat: number;
  /** Key of the duplicate group this row belongs to, if any. */
  dupKey: string | null;
}

export interface CategorySchedule {
  rows: Row[];
  /** dupKey → seat indices, in seat order. Only groups of 2+. */
  groups: Record<string, number[]>;
  /** dupKey → ms at which BOTH chips flip 10 → 5. */
  downgradeAt: Record<string, number>;
  /** Everything is on screen and settled at this ms. */
  revealEndMs: number;
  dwellMs: number;
  totalMs: number;
  /** Did anything worth laughing at happen? */
  hasDrama: boolean;
}

export const rowRevealMs = (seat: number) => TALLY.HEADER_MS + seat * TALLY.ROW_CADENCE_MS;
export const chipRevealMs = (seat: number) => rowRevealMs(seat) + TALLY.CHIP_DELAY_MS;

/**
 * What the chip on `row` reads at time `t`. Exported because this single
 * function IS the joke — a duplicate proudly shows a green 10 until the beat
 * its partner lands, and then both flip to amber 5 together. Worth a test.
 */
export function chipValueAt(row: Row, schedule: CategorySchedule, t: number): number | null {
  if (t < chipRevealMs(row.seat)) return null; // not flipped in yet
  // A host override is the final word: skip the collision theatre and show the
  // settled number straight away. The joke has already been told by then.
  if (row.cell.overridden) return row.cell.points;
  if (row.dupKey && t < schedule.downgradeAt[row.dupKey]) return 10;
  return row.cell.points;
}

export function buildSchedule(
  players: TallyPlayer[],
  category: string,
  answerDetails: TallyRoundData['answerDetails'],
  reduced: boolean,
): CategorySchedule {
  const rows: Row[] = players.map((player, seat) => ({
    player,
    cell: answerDetails?.[player.id]?.[category] || BLANK_CELL,
    seat,
    dupKey: null,
  }));

  // Group by the normalised word. We trust the server's `status`, but we
  // recompute the *grouping* locally because `duplicateOf` gives us peer ids
  // and we want seat indices for the link line.
  const groups: Record<string, number[]> = {};
  for (const row of rows) {
    if (row.cell.status !== 'duplicate' || !row.cell.answer) continue;
    const key = row.cell.answer.trim().toLowerCase();
    row.dupKey = key;
    (groups[key] ||= []).push(row.seat);
  }
  // A "group" of one isn't a collision — drop it (defensive: the server
  // shouldn't produce this, but a host override one day could).
  for (const key of Object.keys(groups)) {
    if (groups[key].length < 2) {
      delete groups[key];
      const row = rows.find(r => r.dupKey === key);
      if (row) row.dupKey = null;
    }
  }

  // The collision resolves once the LAST member of the group has landed and
  // held its doomed green 10 for a beat.
  const downgradeAt: Record<string, number> = {};
  for (const [key, seats] of Object.entries(groups)) {
    const lastSeat = Math.max(...seats);
    downgradeAt[key] = chipRevealMs(lastSeat) + TALLY.COLLISION_HOLD_MS;
  }

  const lastChip = rows.length ? chipRevealMs(rows.length - 1) : 0;
  const lastDowngrade = Object.values(downgradeAt).reduce((a, b) => Math.max(a, b), 0);
  const revealEndMs = Math.max(lastChip, lastDowngrade);

  const hasDrama = rows.some(r => r.cell.status === 'duplicate' || r.cell.status === 'invalid');
  const dwellMs = reduced
    ? 1200
    : hasDrama
      ? TALLY.DWELL_DRAMA_MS
      : TALLY.DWELL_CLEAN_MS;

  return {
    rows,
    groups,
    downgradeAt,
    revealEndMs,
    dwellMs,
    totalMs: revealEndMs + dwellMs,
    hasDrama,
  };
}

/**
 * How long the whole cascade will take, in WALL-CLOCK ms, for this exact
 * payload — every category plus the bridge.
 *
 * It is a pure function of the `roundEnded` payload the server broadcast to
 * everybody, so every client in the room computes the same number without a
 * single extra event. That is what lets a phone in CONTROLLER mode — which is
 * showing "Look up 📺" instead of running the reveal — still know precisely
 * when the television will have finished, and move on with the room instead of
 * stranding its player on a dead screen.
 */
export function cascadeTotalMs(
  players: TallyPlayer[],
  categories: string[],
  answerDetails: TallyRoundData['answerDetails'],
  reduced: boolean,
  paceScale = 1,
): number {
  const timeline = categories.reduce(
    (sum, c) => sum + buildSchedule(players, c, answerDetails, reduced).totalMs,
    0,
  ) + TALLY.BRIDGE_MS;
  return timeline / (paceScale || 1);
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

/**
 * THE CASCADE.
 *
 * The 40 seconds after the round ends — when the family finds out what
 * everyone else wrote — is the product. Not the typing. This component is
 * that moment.
 *
 * It renders on EVERY player's phone, not just the host's, and it is not
 * behind a flag. It runs a deterministic, auto-paced timeline computed
 * purely from the `roundEnded` payload the server already broadcasts to
 * everyone, so no new backend events are needed to keep the table in sync.
 *
 * Tap anywhere to skip ahead. The host can pause.
 */
/** A clock value far past any category's `totalMs` — "fully revealed". */
const REVIEW_CLOCK_MS = 10_000_000;

export function TallyCascade({
  letter,
  categories,
  roundData,
  players,
  isHost,
  onComplete,
  isLastRound = false,
  onOverride,
  paceScale = 1,
  broadcast = false,
}: TallyCascadeProps) {
  const reduced = useMemo(() => prefersReducedMotion(), []);
  const desktop = useSurface() === 'desktop' || broadcast;
  // A television has a keyboard attached in exactly the sense that a billboard
  // does. No hints, no hotkeys.
  const kb = useKeyboardSurface() && !broadcast;
  /** Bigger stage, taller rows — but the SAME beat sheet. See below. */
  const rowH = desktop ? STAGE.TALLY_ROW_H_LG : TALLY.ROW_H;

  const [catIndex, setCatIndex] = useState(0);
  const [stage, setStage] = useState<'categories' | 'bridge'>('categories');
  const [paused, setPaused] = useState(false);
  /**
   * "Wait — go back. What did Dad write?"
   *
   * After the cascade has finished, ←/→ step back and forth through the
   * categories, fully revealed and frozen. It is the single most-requested
   * thing at a real table and a phone has nowhere to put it; a keyboard makes
   * it free. `null` = not reviewing.
   */
  const [review, setReview] = useState<number | null>(null);
  /** Which cell the host is currently settling. Open sheet ⇒ the clock stops. */
  const [overrideTarget, setOverrideTarget] = useState<Row | null>(null);
  // The cascade must NOT keep racing while the host is deciding — otherwise the
  // timeline advances to the next category out from under the open sheet. Same
  // for review: the reveal is frozen while you look back at it.
  const [clock, setClock] = useClock(!paused && !overrideTarget && review === null, paceScale);
  const completedRef = useRef(false);

  const canOverride = isHost && !!onOverride;
  const activeIndex = review ?? catIndex;
  const category = categories[activeIndex];
  const schedule = useMemo(
    () => buildSchedule(players, category, roundData.answerDetails, reduced),
    [players, category, roundData.answerDetails, reduced],
  );
  const stageClock = review !== null ? REVIEW_CLOCK_MS : clock;

  const handleOverride = (points: number) => {
    if (!overrideTarget) return;
    onOverride?.(overrideTarget.player.id, category, points);
    setOverrideTarget(null);
  };

  /* ---- The pop ladder ---------------------------------------------------
     One 60ms pop per row as it lands, each a semitone higher than the last.
     Rising pitch is what turns a list into a count-up — it is the sound of
     the table leaning in.

     It is driven off the SAME clock as the visuals, so the sound cannot drift
     from the row it belongs to. Three things are deliberately not popped:

       · REVIEW. Stepping back through a settled category reveals every row at
         once (`REVIEW_CLOCK_MS`), which would fire the whole ladder as one
         chord. Reviewing is looking, not landing.
       · A SKIP. Tapping to reveal the rest of the category jumps the clock
         past several rows in one frame; they get marked as popped without
         sounding, so an impatient tap is quiet rather than a burst.
       · Anything past the 8th row. A ninth rung is a kettle.

     `pop` is a SHARED sound: if a TV has the room, the service suppresses it
     here and the TV plays it instead. */
  const poppedRef = useRef<Set<number>>(new Set());
  const lastCatRef = useRef<number>(-1);

  useEffect(() => {
    // New category ⇒ a new ladder, from the bottom.
    if (lastCatRef.current !== activeIndex) {
      lastCatRef.current = activeIndex;
      poppedRef.current.clear();
      soundService.resetPops();
    }
    if (review !== null || stage !== 'categories') return;

    for (const row of schedule.rows) {
      const due = rowRevealMs(row.seat);
      if (stageClock < due || poppedRef.current.has(row.seat)) continue;
      poppedRef.current.add(row.seat);
      // Landed on THIS frame (within one row-cadence) — anything older than
      // that arrived via a skip and has already missed its moment.
      const late = stageClock - due;
      if (late <= TALLY.ROW_CADENCE_MS) soundService.pop();
    }
  }, [stageClock, activeIndex, schedule, review, stage]);

  // Advance the timeline. Category → next category → … → bridge → done.
  useEffect(() => {
    if (review !== null) return; // frozen — the user is looking back
    if (stage === 'categories') {
      if (clock < schedule.totalMs) return;
      if (catIndex < categories.length - 1) {
        setCatIndex(i => i + 1);
        setClock(0);
      } else {
        setStage('bridge');
        setClock(0);
      }
      return;
    }
    // bridge
    if (clock >= TALLY.BRIDGE_MS && !completedRef.current) {
      completedRef.current = true;
      onComplete();
    }
  }, [clock, stage, schedule.totalMs, catIndex, categories.length, setClock, onComplete, review]);

  /**
   * Tap anywhere (or press Space): first tap fully reveals the current
   * category (no more waiting on the cadence), second tap moves on. Impatient
   * players get to be impatient without the host having to do anything.
   */
  const handleSkip = () => {
    // A stray cursor on a smart-TV remote must never fast-forward the reveal for
    // the entire room.
    if (broadcast) return;
    if (paused || overrideTarget) return;
    if (review !== null) { setReview(null); return; } // Space leaves review
    if (stage === 'bridge') {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete();
      }
      return;
    }
    if (clock < schedule.revealEndMs) setClock(schedule.revealEndMs);
    else setClock(schedule.totalMs);
  };

  /** Step through the settled categories. Only once the cascade is done. */
  const canReview = stage === 'bridge' || review !== null;
  const step = (d: 1 | -1) => {
    if (!canReview) return;
    if (review === null) {
      // Entering review from the bridge — ← starts at the last category.
      if (d === -1) setReview(categories.length - 1);
      return;
    }
    const next = review + d;
    if (next < 0) return;
    if (next > categories.length - 1) { setReview(null); return; } // → past the end = back to the bridge
    setReview(next);
  };

  useHotkeys(
    [
      { keys: [' ', 'Spacebar'], handler: handleSkip },
      { keys: 'ArrowLeft', handler: () => step(-1), enabled: canReview },
      { keys: 'ArrowRight', handler: () => step(1), enabled: canReview },
    ],
    kb,
  );

  /* ---- The live running totals ------------------------------------------
     This is what a phone genuinely cannot do: the cascade AND the scoreboard,
     at the same time. And it must be computed FROM THE CLOCK, not from the
     server's post-round totals — those are the answer, and printing them
     beside a reveal that hasn't happened yet would spoil every single joke.
     So: previous total + every chip that has actually landed. Which means the
     rail shows a duplicate's doomed green 10 too, and then watches it die.
     The rail participates in the joke; it doesn't leak it. */
  const runningTotals = useMemo(() => {
    const out: Record<string, number> = {};
    for (const p of players) {
      const finalTotal = roundData.playerTotalScores?.[p.id] ?? 0;
      const roundDelta = roundData.scores?.[p.id] ?? 0;
      let n = finalTotal - roundDelta; // where they stood before this round
      if (stage === 'bridge' && review === null) {
        n = finalTotal;
      } else {
        // Categories already fully played out.
        for (let c = 0; c < activeIndex; c++) {
          n += roundData.answerDetails?.[p.id]?.[categories[c]]?.points ?? 0;
        }
        // The category on screen — only what has actually flipped in.
        const row = schedule.rows.find(r => r.player.id === p.id);
        if (row) {
          const v = chipValueAt(row, schedule, stageClock);
          if (v !== null) n += v;
        }
      }
      out[p.id] = n;
    }
    return out;
  }, [players, roundData, categories, activeIndex, schedule, stageClock, stage, review]);

  const body = (
    <div
      className={`wr-col ${desktop ? 'wr-tally-stage' : ''}`.trim()}
      style={{
        flex: 1,
        minHeight: 0,
        position: 'relative',
        // On a television the cascade is a fixed-height thing in a very tall
        // box. Left top-anchored, the whole show huddles in the upper third of a
        // 55-inch screen and the rest is a black rectangle. Centre it.
        ...(broadcast ? { justifyContent: 'center' } : null),
      }}
      onClick={handleSkip}
      role="presentation"
    >
      {/* The ONE ambient decoration a desktop earns: the round's letter,
          enormous, at 3% ink, behind the stage. Static — it does not drift,
          pulse, or parallax. It is wallpaper, and it knows it. */}
      {desktop && <div className="wr-tally-watermark" aria-hidden>{letter}</div>}

      {isHost && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); setPaused(p => !p); }}
          aria-label={paused ? 'Resume the tally' : 'Pause the tally'}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 4,
            width: 34,
            height: 34,
            borderRadius: 10,
            border: '1px solid var(--line-2)',
            background: paused ? 'var(--accent)' : 'var(--surface)',
            color: paused ? '#fff' : 'var(--ink-3)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          {paused ? <Play size={15} strokeWidth={2.4} /> : <Pause size={15} strokeWidth={2.4} />}
        </button>
      )}

      <AnimatePresence mode="wait">
        {stage === 'categories' || review !== null ? (
          <CategoryStage
            key={`cat-${activeIndex}`}
            letter={letter}
            category={category}
            index={activeIndex}
            total={categories.length}
            schedule={schedule}
            clock={stageClock}
            reduced={reduced}
            canOverride={canOverride}
            onPickOverride={setOverrideTarget}
            rowH={rowH}
            desktop={desktop}
            reviewing={review !== null}
            // Space skips a beat at ANY point in the cascade — so say so at any
            // point in the cascade, not only once it's over.
            showStepHint={kb}
            broadcast={broadcast}
          />
        ) : (
          <BridgeStage
            key="bridge"
            roundData={roundData}
            players={players}
            clock={clock}
            isLastRound={isLastRound}
            reduced={reduced}
            showStepHint={kb}
            broadcast={broadcast}
          />
        )}
      </AnimatePresence>

      <OverrideSheet
        row={overrideTarget}
        category={category}
        onPick={handleOverride}
        onCancel={() => setOverrideTarget(null)}
      />
    </div>
  );

  if (!desktop) return body;

  return (
    <StageLayout
      main="wide"
      right={
        <TotalsRail
          players={players}
          totals={runningTotals}
          reduced={reduced}
        />
      }
    >
      {body}
    </StageLayout>
  );
}

/* ------------------------------------------------------------------ */
/* The sticky, live-re-sorting scoreboard                              */
/* ------------------------------------------------------------------ */

function TotalsRail({
  players,
  totals,
  reduced,
}: {
  players: TallyPlayer[];
  totals: Record<string, number>;
  reduced: boolean;
}) {
  const rows = [...players].sort((a, b) => (totals[b.id] ?? 0) - (totals[a.id] ?? 0));
  return (
    <div className="wr-roster wr-tally-totals">
      <div className="wr-between" style={{ marginBottom: 2 }}>
        <span className="eyebrow">Running total</span>
      </div>
      {rows.map((p, i) => (
        <motion.div
          key={p.id}
          layout={!reduced}
          transition={SPRING_SORT as any}
          className="roster-row"
        >
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 12,
              fontWeight: 800,
              width: 14,
              color: i === 0 ? 'var(--accent)' : 'var(--ink-3)',
            }}
          >
            {i + 1}
          </span>
          <Avatar id={p.avatar} size={30} />
          <span className="rname">{p.name}</span>
          <span className="rtotal">{totals[p.id] ?? 0}</span>
        </motion.div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The host override sheet                                             */
/* ------------------------------------------------------------------ */

/**
 * "Your table, your call."
 *
 * The whole reason strict validation is survivable. The wordlist will reject
 * something real — a name, a regional spelling, "orangatang" — and the host
 * fixes it in two taps. The emit goes to the SERVER; every phone re-flips.
 */
function OverrideSheet({
  row,
  category,
  onPick,
  onCancel,
}: {
  row: Row | null;
  category: string;
  onPick: (points: number) => void;
  onCancel: () => void;
}) {
  return (
    <AnimatePresence>
      {row && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={e => { e.stopPropagation(); onCancel(); }}
          role="dialog"
          aria-modal="true"
          aria-label="Override score"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 220,
            background: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
            onClick={e => e.stopPropagation()}
            className="card lg"
            style={{
              width: 'calc(100% - 32px)',
              maxWidth: 440,
              margin: 16,
              background: 'var(--surface)',
              padding: 22,
            }}
          >
            <div className="eyebrow accent">OVERRIDE</div>
            <div className="h3" style={{ marginTop: 6, lineHeight: 1.25 }}>
              {row.player.name} — “{row.cell.answer || '—'}”
            </div>
            <div className="wr-text-3" style={{ fontSize: 12, marginTop: 4 }}>
              {category} · {row.cell.status === 'invalid'
                ? (row.cell.reason || 'the validator rejected this')
                : row.cell.status === 'duplicate'
                  ? 'matched someone else'
                  : row.cell.status === 'blank'
                    ? 'left blank'
                    : 'counted'}
            </div>

            <div className="wr-row gap-2 mt-4" style={{ justifyContent: 'space-between' }}>
              {OVERRIDE_CHOICES.map(pts => {
                const active = row.cell.points === pts;
                const kind = chipKindFor(pts);
                return (
                  <button
                    key={pts}
                    type="button"
                    onClick={() => onPick(pts)}
                    aria-label={`Award ${pts} points`}
                    style={{
                      flex: 1,
                      height: 64,
                      borderRadius: 18,
                      cursor: 'pointer',
                      fontFamily: 'var(--display)',
                      fontWeight: 800,
                      fontSize: 26,
                      border: active ? '2.5px solid var(--accent)' : '1.5px solid var(--line-2)',
                      background:
                        kind === 'green' ? 'var(--green-soft)'
                          : kind === 'amber' ? 'var(--amber-soft)'
                            : 'var(--slate-soft)',
                      color:
                        kind === 'green' ? 'var(--green)'
                          : kind === 'amber' ? 'var(--amber)'
                            : 'var(--slate)',
                    }}
                  >
                    {pts}
                  </button>
                );
              })}
            </div>

            <div
              className="wr-text-3"
              style={{ fontSize: 12, textAlign: 'center', marginTop: 14, fontStyle: 'italic' }}
            >
              Your table, your call.
            </div>

            <button
              type="button"
              onClick={onCancel}
              style={{
                width: '100%',
                marginTop: 12,
                padding: '12px 16px',
                borderRadius: 14,
                border: 'none',
                background: 'transparent',
                color: 'var(--ink-3)',
                fontFamily: 'var(--ui)',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Leave it alone
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ------------------------------------------------------------------ */
/* Stage 1 — one category                                              */
/* ------------------------------------------------------------------ */

function CategoryStage({
  letter,
  category,
  index,
  total,
  schedule,
  clock,
  reduced,
  canOverride,
  onPickOverride,
  rowH = TALLY.ROW_H,
  desktop = false,
  reviewing = false,
  showStepHint = false,
  broadcast = false,
}: {
  letter: string;
  category: string;
  index: number;
  total: number;
  schedule: CategorySchedule;
  clock: number;
  reduced: boolean;
  canOverride: boolean;
  onPickOverride: (row: Row) => void;
  /** 64 on a phone, 80 on a desktop. Feeds the collision-line geometry. */
  rowH?: number;
  desktop?: boolean;
  /** Frozen, fully revealed, being stepped through with ←/→. */
  reviewing?: boolean;
  showStepHint?: boolean;
  /** This is a television: no hints, no instructions, no affordances. */
  broadcast?: boolean;
}) {
  const Icon = CATEGORY_ICON[category] || (() => null);
  const dwellProgress = Math.min(
    1,
    Math.max(0, (clock - schedule.revealEndMs) / Math.max(1, schedule.dwellMs)),
  );
  const containerH = schedule.rows.length * rowH + Math.max(0, schedule.rows.length - 1) * TALLY.ROW_GAP;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: DUR.fast / 1000 }}
      className="wr-col"
      style={{
        flex: 1,
        minHeight: 0,
        // The stage stretches; its contents do not. On a television that leaves
        // the reveal pinned to the top edge with half a screen of black beneath
        // it. Centre the show on the screen it is being shown on.
        ...(broadcast ? { justifyContent: 'center' } : null),
      }}
    >
      {/* A matching spacer ABOVE the header, on a television only.
          There is already a `flex: 1` spacer below the rows (it pins the dwell
          bar to the bottom edge), and one spacer is all it takes to make
          `justify-content: center` a no-op: the content is already filling the
          box. Two spacers sandwich the reveal and it lands in the middle of the
          screen, which on a 55-inch panel is the difference between a broadcast
          and a browser window. */}
      {broadcast && <div style={{ flex: 1 }} />}

      {/* t=0 — the header stamps in, full width, alone. */}
      <motion.div
        initial={reduced ? false : { scale: 1.14, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.32, ease: EASE.back as any }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          padding: '14px 16px',
          borderRadius: 18,
          background: 'var(--accent-soft)',
          color: 'var(--accent)',
          marginTop: 8,
        }}
      >
        <Icon size={20} color="var(--accent)" />
        <span
          style={{
            fontFamily: 'var(--display)',
            fontWeight: 800,
            fontSize: 26,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          {category}
        </span>
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 12,
            fontWeight: 700,
            opacity: 0.65,
            marginLeft: 4,
          }}
        >
          {letter} · {index + 1}/{total}
        </span>
      </motion.div>

      {/* Rows land in seat order. Fixed geometry so the collision link line
          can be positioned arithmetically instead of by measuring the DOM
          mid-animation. */}
      <div
        style={{
          position: 'relative',
          height: containerH,
          marginTop: 18,
          paddingLeft: 18,
          flexShrink: 0,
        }}
      >
        <CollisionLines schedule={schedule} clock={clock} rowH={rowH} />
        {schedule.rows.map(row => (
          <TallyRow
            key={row.player.id}
            row={row}
            schedule={schedule}
            clock={clock}
            reduced={reduced}
            canOverride={canOverride}
            onPickOverride={onPickOverride}
            rowH={rowH}
            desktop={desktop}
          />
        ))}
      </div>

      <div style={{ flex: 1 }} />

      {/* Thin dwell bar. It drains, then we move on. No tap required — the
          host's job is to intervene, not to click through four categories.
          While REVIEWING, nothing is draining: the bar would be a lie. */}
      {!reviewing && (
        <div
          style={{
            height: 3,
            borderRadius: 999,
            background: 'var(--line)',
            overflow: 'hidden',
            marginBottom: 6,
          }}
          aria-hidden
        >
          <div
            style={{
              height: '100%',
              width: `${(1 - dwellProgress) * 100}%`,
              background: schedule.hasDrama ? 'var(--amber)' : 'var(--accent)',
              transition: 'width 80ms linear',
            }}
          />
        </div>
      )}
      {/* The phone gets EXACTLY the block it always had. The flex row (and the
          keycaps in it) only exist where a keyboard does — and a TELEVISION gets
          none of it. "Click to skip ahead", printed across a living-room wall,
          is an instruction to nobody. */}
      {broadcast ? null : desktop ? (
        <div
          className="wr-text-3 wr-row wr-center gap-2"
          style={{ fontSize: 11, textAlign: 'center', paddingBottom: 6, flexWrap: 'wrap' }}
        >
          {reviewing ? (
            <>
              <Kbd>←</Kbd><Kbd>→</Kbd>
              <span>Reviewing {category} — <Kbd>Space</Kbd> to carry on</span>
            </>
          ) : (
            <>
              <span>
                {canOverride ? 'Click a score to change it · click anywhere else to skip' : 'Click to skip ahead'}
              </span>
              {showStepHint && <Kbd>Space</Kbd>}
            </>
          )}
        </div>
      ) : (
        <div
          className="wr-text-3"
          style={{ fontSize: 11, textAlign: 'center', paddingBottom: 6 }}
        >
          {canOverride ? 'Tap a score to change it · tap anywhere else to skip' : 'Tap to skip ahead'}
        </div>
      )}
    </motion.div>
  );
}

/**
 * The link line between colliding rows. It only draws once the collision has
 * actually resolved — drawing it early would spoil the joke.
 */
function CollisionLines({
  schedule,
  clock,
  rowH = TALLY.ROW_H,
}: {
  schedule: CategorySchedule;
  clock: number;
  rowH?: number;
}) {
  const active = Object.entries(schedule.groups).filter(
    ([key]) => clock >= schedule.downgradeAt[key],
  );
  if (active.length === 0) return null;

  return (
    <>
      {active.map(([key, seats]) => {
        // Taller rows ⇒ a longer line, for free. Same 280ms draw. "Same
        // durations, bigger travel" is the whole desktop motion rule.
        const top = rowCenterY(Math.min(...seats), rowH);
        const bottom = rowCenterY(Math.max(...seats), rowH);
        return (
          <motion.div
            key={key}
            initial={{ scaleY: 0, opacity: 0 }}
            animate={{ scaleY: 1, opacity: 1 }}
            transition={{ duration: 0.28, ease: EASE.out as any }}
            style={{
              position: 'absolute',
              left: 6,
              top,
              height: Math.max(2, bottom - top),
              width: 3,
              borderRadius: 999,
              background: 'var(--amber)',
              transformOrigin: 'top center',
              zIndex: 1,
            }}
          />
        );
      })}
    </>
  );
}

function TallyRow({
  row,
  schedule,
  clock,
  reduced,
  canOverride,
  onPickOverride,
  rowH = TALLY.ROW_H,
  desktop = false,
}: {
  row: Row;
  schedule: CategorySchedule;
  clock: number;
  reduced: boolean;
  canOverride: boolean;
  onPickOverride: (row: Row) => void;
  rowH?: number;
  desktop?: boolean;
}) {
  const visible = clock >= rowRevealMs(row.seat);

  // THE COLLISION. Until the group's downgrade beat, a duplicate row proudly
  // shows a green 10. Then both chips flip to amber 5 at the same instant.
  const displayed = chipValueAt(row, schedule, clock);
  const chipVisible = displayed !== null;
  const downgraded = row.dupKey ? clock >= schedule.downgradeAt[row.dupKey] : false;

  // An overridden word has been settled by the table. It is not "wrong" any
  // more — so it stops being struck through and stops being greyed out, on
  // every phone. (It kept both while displaying a green 10 before, which read
  // as a bug rather than a ruling.)
  const overridden = !!row.cell.overridden || row.cell.status === 'override';
  const isBlank = row.cell.status === 'blank' && !overridden;
  const isInvalid = row.cell.status === 'invalid' && !overridden;
  // Dimmed like anything that scored nothing — but never STRUCK THROUGH, and
  // never offered to the host as override bait. A ban is a rule, not a ruling:
  // there is nothing to settle, and un-banning it for one player would be the
  // only unfair thing the ban list could possibly do.
  const isBanned = row.cell.status === 'banned';
  const dim = isBlank || isInvalid || isBanned;

  const chipKind = chipKindFor(displayed ?? 0);
  // Override bait: on the HOST's screen only, a rejected word wears a dotted
  // ring. It is the single most likely thing they'll want to argue with, so
  // make it look like the button it is. Nobody else sees this.
  const bait = canOverride && isInvalid && chipVisible;

  return (
    <motion.div
      initial={false}
      animate={
        visible
          ? { opacity: dim ? 0.55 : 1, y: 0 }
          : { opacity: 0, y: reduced ? 0 : 10 }
      }
      transition={{ duration: reduced ? 0 : 0.28, ease: EASE.out as any }}
      className="wr-tally-row"
      style={{
        position: 'absolute',
        top: row.seat * (rowH + TALLY.ROW_GAP),
        left: 0,
        right: 0,
        height: rowH,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '0 14px',
        borderRadius: 16,
        background: 'var(--surface)',
        boxShadow: 'var(--shadow-sm)',
        border: downgraded ? '1.5px solid var(--amber)' : '1.5px solid transparent',
        zIndex: 2,
      }}
    >
      <Avatar id={row.player.avatar} size={desktop ? 44 : 36} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--ink-3)',
            letterSpacing: '0.03em',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {row.player.name}
          {downgraded && !overridden && <LinkIcon size={11} color="var(--amber)" />}
          {overridden && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: 'var(--accent)', fontWeight: 700 }}>
              <Gavel size={10} /> host
            </span>
          )}
        </div>
        {/* THE WORD IS THE HERO. Big type, first thing you read. It is the
            one thing that gets *materially* bigger on a desktop — 22 → 32 —
            because the word is what the room is looking at. Body type stays
            exactly where it is. */}
        <div
          className={desktop ? 'wr-tally-word' : undefined}
          style={{
            fontFamily: 'var(--display)',
            fontWeight: 800,
            fontSize: isBlank ? 20 : 22,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            color: dim ? 'var(--ink-3)' : 'var(--ink-1)',
            textDecoration: isInvalid ? 'line-through' : 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row.cell.answer || '—'}
        </div>
        {isInvalid && (
          <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
            {row.cell.reason || `doesn't count`}
          </div>
        )}
        {/* You knew the word. You were not allowed it. Say the true thing. */}
        {isBanned && (
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--coral, #b94a3b)' }}>
            banned this round
          </div>
        )}
        {overridden && (
          <div style={{ fontSize: 10, color: 'var(--accent)' }}>the table allowed it</div>
        )}
      </div>

      {/* The host's settle affordance, desktop only. It fades in under the
          cursor on ANY settled row — not just the ones the validator hated —
          because the argument at a real table is never confined to the words
          a wordlist happens to dislike.

          It is NOT load-bearing: the dotted "bait" ring below is still on the
          chip for touch-at-lg, the chip itself is still tappable, and the
          phone keeps its always-visible affordance. Hover only ever ADDS a
          path here; it never becomes the only one. */}
      {desktop && canOverride && chipVisible && (
        <button
          type="button"
          className="wr-settle"
          onClick={e => { e.stopPropagation(); onPickOverride(row); }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '6px 10px',
            minHeight: 32,
            borderRadius: 999,
            border: '1px solid var(--line-2)',
            background: 'var(--surface-2)',
            color: 'var(--ink-3)',
            fontFamily: 'var(--ui)',
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <Gavel size={13} /> Settle…
        </button>
      )}

      <AnimatePresence>
        {chipVisible && (
          <motion.span
            // Keying on the VALUE (not just the colour band) is what makes the
            // chip visibly re-flip when the server confirms an override —
            // 0 → 10 stays 'slate' → 'green', but 5 → 5 after an override on a
            // duplicate would otherwise never remount and never animate.
            key={`${chipKind}-${displayed}`}
            className={`score lg ${chipKind === 'danger' ? '' : chipKind}`}
            role={canOverride ? 'button' : undefined}
            tabIndex={canOverride ? 0 : undefined}
            aria-label={
              canOverride
                ? `${row.player.name} scored ${displayed} for “${row.cell.answer || 'blank'}” — tap to override`
                : undefined
            }
            onClick={canOverride ? (e => { e.stopPropagation(); onPickOverride(row); }) : undefined}
            initial={reduced ? false : { rotateX: -90, opacity: 0, scale: 0.8 }}
            animate={{ rotateX: 0, opacity: 1, scale: 1 }}
            transition={reduced ? { duration: 0 } : (SPRING_CHIP as any)}
            style={{
              minWidth: 46,
              textAlign: 'center',
              flexShrink: 0,
              cursor: canOverride ? 'pointer' : undefined,
              ...(chipKind === 'danger' ? DANGER_CHIP_STYLE : null),
              ...(bait ? { outline: '2px dotted var(--accent)', outlineOffset: 3 } : null),
            }}
          >
            {displayed}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Stage 2 — the bridge                                                */
/* ------------------------------------------------------------------ */

function BridgeStage({
  roundData,
  players,
  clock,
  isLastRound,
  reduced,
  showStepHint = false,
  broadcast = false,
}: {
  roundData: TallyRoundData;
  players: TallyPlayer[];
  clock: number;
  isLastRound: boolean;
  reduced: boolean;
  showStepHint?: boolean;
  /** This is a television. Nobody is going to tap it. */
  broadcast?: boolean;
}) {
  const standings = useMemo(() => {
    const rows = players.map(p => {
      const total = roundData.playerTotalScores?.[p.id] ?? 0;
      const delta = roundData.scores?.[p.id] ?? 0;
      return { player: p, total, delta, prev: total - delta };
    });
    const byPrev = [...rows].sort((a, b) => b.prev - a.prev);
    const prevRank = new Map(byPrev.map((r, i) => [r.player.id, i]));
    const byNow = [...rows].sort((a, b) => b.total - a.total);
    return byNow.map((r, i) => ({
      ...r,
      rank: i,
      // Positive = climbed.
      move: (prevRank.get(r.player.id) ?? i) - i,
    }));
  }, [players, roundData]);

  const secondsLeft = Math.max(0, Math.ceil((TALLY.BRIDGE_MS - clock) / 1000));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: DUR.base / 1000 }}
      className="wr-col"
      style={{ flex: 1, minHeight: 0 }}
    >
      <div className="eyebrow accent" style={{ marginTop: 10, textAlign: 'center' }}>
        AFTER ROUND {roundData.roundNumber}
      </div>

      <div className="wr-col gap-2 mt-3" style={{ overflowY: 'auto', minHeight: 0 }}>
        {standings.map(s => (
          <motion.div
            key={s.player.id}
            layout={!reduced}
            transition={SPRING_SORT as any}
            className="prow"
            style={{ padding: '10px 14px' }}
          >
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 12,
                fontWeight: 800,
                width: 18,
                color: s.rank === 0 ? 'var(--accent)' : 'var(--ink-3)',
              }}
            >
              {s.rank + 1}
            </span>
            <Avatar id={s.player.avatar} size={34} />
            <div className="body" style={{ minWidth: 0 }}>
              <div className="name" style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {s.player.name}
                </span>
                {s.move > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--green)', fontSize: 11, fontWeight: 700 }}>
                    <ChevronUp size={12} strokeWidth={3} />{s.move}
                  </span>
                )}
                {s.move < 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--ink-3)', fontSize: 11, fontWeight: 700 }}>
                    <ChevronDown size={12} strokeWidth={3} />{Math.abs(s.move)}
                  </span>
                )}
              </div>
            </div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-3)', marginRight: 8 }}>
              +{s.delta}
            </span>
            <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18 }}>
              {s.total}
            </span>
          </motion.div>
        ))}
      </div>

      <div style={{ flex: 1 }} />

      <div
        className="card"
        style={{
          padding: 14,
          marginBottom: 8,
          textAlign: 'center',
          background: 'var(--bg-2)',
          boxShadow: 'none',
        }}
      >
        <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 16 }}>
          {isLastRound ? 'See the winner…' : `Next round in ${secondsLeft}…`}
        </div>
        {/* "Wait — go back. What did Dad write?" — desktop only, and the
            phone's line is byte-for-byte the one it always had. A television is
            told nothing: it has no tapper and no keyboard. */}
        {broadcast ? null : showStepHint ? (
          <div className="wr-text-3 wr-row wr-center gap-2" style={{ fontSize: 11, marginTop: 2, flexWrap: 'wrap' }}>
            <span>Click anywhere to go now</span>
            <span className="wr-row gap-1" style={{ alignItems: 'center' }}>
              · <Kbd>←</Kbd> to look back
            </span>
          </div>
        ) : (
          <div className="wr-text-3" style={{ fontSize: 11, marginTop: 2 }}>
            Tap anywhere to go now
          </div>
        )}
      </div>
    </motion.div>
  );
}
